import { createTheme, type Theme } from '@mui/material/styles';
import { esquemaMarca } from '@exp/contracts';
import { CHASIS } from '../diseno/tokens';
import { derivarAcento, type Acento } from '../estilos/acento';
import { hexARgb } from '../estilos/color';
import { colorMui } from '../estilos/hojas';
import { PULSO } from '../producto/identidad';
import type { Marca } from './tipos';

/**
 * Identidad visual por defecto, mientras no se conoce la del consultorio.
 *
 * Es la del PRODUCTO, Pulso (ADR-013), nunca la de un cliente concreto. Se
 * valida contra el contrato (`esquemaMarca`) para que tenga la misma forma que
 * la marca de un consultorio; aqui no se escribe ningun hex.
 */
const POR_DEFECTO = esquemaMarca.parse({
  nombreComercial: PULSO.nombre,
  colorPrimario: PULSO.colorPrimario,
  colorSecundario: PULSO.colorSecundario,
});

export const MARCA_POR_DEFECTO: Marca = {
  nombreComercial: POR_DEFECTO.nombreComercial,
  colorPrimario: POR_DEFECTO.colorPrimario,
  colorSecundario: POR_DEFECTO.colorSecundario,
  logoUrl: null,
  membrete: { direccion: '', telefono: '', web: '', correo: '' },
  pieDePagina: '',
};

/** Modo efectivo con el que se pinta. */
export type ModoDeTema = 'claro' | 'oscuro';
/** Lo que elige la persona: un modo fijo, o seguir al sistema. */
export type PreferenciaDeTema = ModoDeTema | 'automatico';

/**
 * `fontWeight` de MUI esta tipado como numero; los pesos de la compensacion
 * oscura (420/520) viven en variables CSS. El valor llega intacto al CSS.
 */
const peso = (rol: string) => rol as unknown as number;

/**
 * Construye el tema de MUI.
 *
 * ## Una sola fuente de verdad: las hojas de roles
 *
 * Los colores viven en `estilos/paleta.css` (nivel 1) y `estilos/semantico.css`
 * (roles). Este archivo no tiene ningun hex propio.
 *
 * Decision sobre MUI y las variables CSS (documentada porque es la trampa
 * obvia): MUI llama `alpha()`, `darken()`, `lighten()` y `emphasize()` sobre
 * `palette.*` —hover de IconButton, seleccion de ToggleButton y TableRow,
 * Alert, Snackbar, contraste de texto— y esas funciones TRUENAN con
 * `'var(--x)'` y no entienden `oklch()`. Se descarto:
 *
 * - `cssVariables` + `colorSchemes` de MUI 6: sigue exigiendo colores
 *   concretos en la paleta de entrada (calcula canales RGB), asi que obligaria
 *   a duplicar los valores de las hojas en TypeScript.
 * - Copiar los valores a TS: dos fuentes que acabarian discrepando.
 *
 * Solucion: dos capas con un mismo origen.
 *
 * 1. `palette.*` recibe valores CONCRETOS en sRGB (`#rrggbb` / `rgba()`),
 *    obtenidos leyendo las propias hojas (`estilos/hojas.ts`, importadas
 *    `?raw`) y, para el acento, de la rampa calculada (`estilos/acento.ts`).
 *    Solo sirven para lo que MUI calcula por dentro.
 * 2. Todo `styleOverrides` escribe `var(--rol)`, de modo que lo que se ve lo
 *    decide el CSS en vivo (modo, marca, puntero coarse, movimiento reducido).
 *
 * Los componentes propios leen roles en `sx` (`'var(--text-secondary)'`), no
 * `palette.*`. La paleta de MUI es un detalle de implementacion de MUI.
 *
 * ## El acento es del consultorio; lo demas es del producto
 *
 * El acento (boton principal, destino activo, seleccion y foco) sale de `marca.colorPrimario` via `derivarAcento`, que
 * ajusta la luminosidad para garantizar AA. Los neutros, los estados, la
 * gravedad clinica, la tipografia y la densidad son iguales en todos los
 * despliegues: por eso dos consultorios con paletas opuestas se sienten la
 * misma herramienta.
 */
export function construirTema(
  marca: Marca,
  modo: ModoDeTema = 'claro',
  acento: Acento = derivarAcento(marca.colorPrimario),
): Theme {
  const c = (rol: string) => colorMui(rol, modo, acento);
  const secundario = hexARgb(marca.colorSecundario) ? marca.colorSecundario : acento.mui[modo].main;

  const estado = (nombre: 'success' | 'warning' | 'danger' | 'info') => ({
    main: c(`--status-${nombre}-solid`),
    dark: c(`--status-${nombre}-text`),
    light: c(`--status-${nombre}-border`),
  });

  // Insignia de estado: lavado + texto + borde del mismo estado.
  const insignia = (nombre: 'success' | 'warning' | 'danger' | 'info') => ({
    backgroundColor: `var(--status-${nombre}-wash)`,
    color: `var(--status-${nombre}-text)`,
    borderColor: `var(--status-${nombre}-border)`,
  });

  const anilloDeFoco = {
    outline: '2px solid var(--focus-color)',
    outlineOffset: 2,
  };

  const panelFlotante = {
    backgroundColor: 'var(--bg-raised)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-3)',
    backgroundImage: 'none',
  };

  return createTheme({
    palette: {
      mode: modo === 'claro' ? 'light' : 'dark',
      contrastThreshold: 4.5,
      primary: acento.mui[modo],
      secondary: { main: secundario },
      success: estado('success'),
      warning: estado('warning'),
      error: estado('danger'),
      info: estado('info'),
      divider: c('--border-hairline'),
      text: {
        primary: c('--text-primary'),
        secondary: c('--text-secondary'),
        disabled: c('--text-disabled'),
      },
      background: { default: c('--bg-canvas'), paper: c('--bg-raised') },
      action: {
        active: c('--text-secondary'),
        hover: c('--bg-hover'),
        selected: c('--bg-selected'),
        focus: c('--bg-active'),
        disabled: c('--text-disabled'),
        disabledBackground: c('--bg-sunken'),
      },
    },

    // Escala del sistema visual. Fija en rem, sin clamp por viewport. Nada que
    // haya que leer para trabajar baja de 13px (0.8125rem).
    typography: {
      fontFamily: 'var(--font-sans)',
      h1: {
        fontFamily: 'var(--font-head)',
        fontSize: 'var(--text-title)',
        fontWeight: 600,
        lineHeight: 'var(--leading-tight)',
        letterSpacing: 'var(--tracking-tight)',
        '@media (max-width: 780px)': { fontSize: '1.5rem' },
      },
      h2: {
        fontFamily: 'var(--font-head)',
        fontSize: 'var(--text-headline)',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
        letterSpacing: 'var(--tracking-snug)',
      },
      h3: {
        fontFamily: 'var(--font-head)',
        fontSize: 'var(--text-panel-title)',
        fontWeight: 700,
        lineHeight: 'var(--leading-snug)',
        letterSpacing: 'var(--tracking-snug)',
      },
      h4: {
        fontFamily: 'var(--font-head)',
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
        letterSpacing: 'var(--tracking-snug)',
      },
      h5: {
        fontSize: 'var(--text-body-dense)',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
      },
      h6: {
        fontSize: 'var(--text-body-dense)',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
      },
      subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 'var(--leading-snug)' },
      subtitle2: {
        fontSize: 'var(--text-body-dense)',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
      },
      body1: {
        fontSize: 'var(--density-font-body)',
        fontWeight: peso('var(--weight-body)'),
        lineHeight: 'var(--leading-body)',
        letterSpacing: 'var(--tracking-body)',
      },
      body2: {
        fontSize: 'var(--text-label)',
        fontWeight: peso('var(--weight-body)'),
        lineHeight: 1.5,
        letterSpacing: 'var(--tracking-body)',
      },
      // `caption` se usa para contexto operativo (fechas, notas de campo):
      // 13px, no 12.
      caption: {
        fontSize: 'var(--text-label)',
        fontWeight: peso('var(--weight-body)'),
        lineHeight: 1.4,
        letterSpacing: 'var(--tracking-body)',
      },
      // Rotulo de seccion. Se queda en 13px porque ordena formularios clinicos:
      // no es un metadato que se pueda ignorar.
      overline: {
        fontSize: 'var(--text-label)',
        fontWeight: 600,
        lineHeight: 'var(--leading-snug)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      button: {
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-body-dense)',
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: 0,
        textTransform: 'none',
      },
    },

    // Base pequena: los radios del sistema se leen por rol
    // (`'var(--radius-card)'`), no multiplicando este numero en `sx`.
    shape: { borderRadius: 4 },

    // Plano antes que sombra. En oscuro, shadow-1/2 son transparentes.
    shadows: [
      'none',
      'var(--shadow-1)',
      ...Array.from({ length: 2 }, () => 'var(--shadow-2)'),
      ...Array.from({ length: 12 }, () => 'var(--shadow-3)'),
      ...Array.from({ length: 9 }, () => 'var(--shadow-4)'),
    ] as Theme['shadows'],

    transitions: {
      easing: {
        easeOut: 'cubic-bezier(0.2, 0, 0, 1)',
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 90,
        shorter: 160,
        short: 160,
        standard: 220,
        complex: 320,
        enteringScreen: 220,
        leavingScreen: 160,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--density-font-body)',
            fontWeight: 'var(--weight-body)',
            lineHeight: 'var(--leading-body)',
            letterSpacing: 'var(--tracking-body)',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--bg-canvas)',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': {
            backgroundColor: 'var(--accent-wash)',
            color: 'var(--accent-text)',
          },
          // Suelo de calidad: el foco SIEMPRE se ve. Contorno y no box-shadow
          // para no chocar con las sombras propias de cada componente; el
          // desfase de 2px deja ver el fondo como separacion.
          ':focus-visible': anilloDeFoco,
          ':focus:not(:focus-visible)': { outline: 'none' },
          '[disabled], [aria-disabled="true"]': { cursor: 'not-allowed' },
          'input[type="checkbox"], input[type="radio"]': { accentColor: 'var(--accent-emphasis)' },
          // Identificadores clinicos (expediente, folio, cedula, huella) en
          // monoespaciada con cifras tabulares: se comparan digito a digito.
          '.dato': {
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: "'tnum' 1",
            letterSpacing: 0,
          },
          // Toda cifra que se compara en columna.
          '.cifra': {
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: "'tnum' 1",
          },
          // Se conserva por compatibilidad: la condensada se retiro y `.denso`
          // ya es la letra de texto.
          '.denso': { fontFamily: 'var(--font-sans)' },
          // La persona y el acta: nombre de paciente y titulo de documento.
          // Doble clase: gana a la clase generada de Typography.
          '.serif, .serif.MuiTypography-root': {
            fontFamily: 'var(--font-serif)',
            letterSpacing: 0,
          },
          // Enlace de salto al contenido: invisible hasta que recibe foco.
          '.saltar-al-contenido': {
            position: 'absolute',
            left: 8,
            top: -60,
            zIndex: 'var(--z-tooltip)',
            padding: '8px 12px',
            borderRadius: 'var(--radius-field)',
            backgroundColor: 'var(--bg-inverted)',
            color: 'var(--text-on-inverted)',
            fontWeight: 600,
            textDecoration: 'none',
            '&:focus': { top: 8 },
          },
          // Oculto a la vista, legible para el lector de pantalla.
          '.solo-lector': {
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '1ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '1ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          '@media print': {
            '.no-imprimir': { display: 'none !important' },
            body: { background: 'transparent' },
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--border-strong) transparent',
          },
        },
      },

      // Sin onda: el estado se dice con color, contorno y un paso de 1px.
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: { '&.Mui-focusVisible': anilloDeFoco },
        },
      },

      // Boton en pildora (sistema de Pulso): 40px (44 con puntero coarse), 14px/600.
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            height: 'var(--density-control-h)',
            minHeight: 'var(--target-min)',
            paddingInline: 'calc(var(--density-control-px) + 4px)',
            gap: 6,
            border: '1px solid transparent',
            borderRadius: 'var(--radius-control)',
            whiteSpace: 'nowrap',
            transition: 'var(--transition-colors)',
            '&:active:not(.Mui-disabled)': { transform: 'translateY(1px)' },
            '&.Mui-disabled': { opacity: 0.55 },
            '& .MuiButton-startIcon': { marginLeft: -2, marginRight: 0 },
            '& .MuiButton-endIcon': { marginLeft: 0, marginRight: -2 },
            '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': {
              fontSize: 18,
            },
            '&[aria-busy="true"]': { cursor: 'progress' },
            variants: [
              // Primario: el acento del consultorio.
              {
                props: { variant: 'contained', color: 'primary' },
                style: {
                  backgroundColor: 'var(--accent-deep)',
                  color: 'var(--accent-on)',
                  '&:hover': { backgroundColor: 'var(--accent-night)' },
                  '&:active': { backgroundColor: 'var(--accent-active)' },
                  '&.Mui-disabled': {
                    backgroundColor: 'var(--accent-deep)',
                    color: 'var(--accent-on)',
                  },
                },
              },
              // Peligro: solo destructivo irreversible.
              {
                props: { variant: 'contained', color: 'error' },
                style: {
                  // `-fill` y no `-solid`: en oscuro el solid (brick-5) da 3.9:1 con blanco.
                  backgroundColor: 'var(--status-danger-fill)',
                  color: 'var(--accent-on)',
                  '&:hover': { backgroundColor: 'var(--status-danger-hover)' },
                  '&.Mui-disabled': {
                    backgroundColor: 'var(--status-danger-fill)',
                    color: 'var(--accent-on)',
                  },
                },
              },
              // Secundario: tarjeta con filete.
              {
                props: { variant: 'outlined' },
                style: {
                  backgroundColor: 'var(--bg-raised)',
                  color: 'var(--text-primary)',
                  borderColor: 'var(--border-subtle)',
                  '&:hover': {
                    backgroundColor: 'var(--bg-hover)',
                    borderColor: 'var(--border-strong)',
                  },
                  '&.Mui-disabled': {
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-subtle)',
                  },
                },
              },
              {
                props: { variant: 'outlined', color: 'error' },
                style: {
                  color: 'var(--status-danger-text)',
                  borderColor: 'var(--status-danger-border)',
                  '&:hover': { borderColor: 'var(--status-danger-solid)' },
                },
              },
              // Texto con color primario: se lee como enlace de accion.
              {
                props: { variant: 'text', color: 'primary' },
                style: {
                  color: 'var(--accent-text)',
                  '&:hover': { backgroundColor: 'var(--bg-hover)' },
                },
              },
              // Fantasma.
              {
                props: { variant: 'text', color: 'inherit' },
                style: {
                  color: 'var(--text-secondary)',
                  '&:hover': { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' },
                },
              },
              {
                props: { variant: 'text', color: 'error' },
                style: {
                  color: 'var(--status-danger-text)',
                  '&:hover': { backgroundColor: 'var(--status-danger-wash)' },
                },
              },
            ],
          },
          // Boton de fila.
          sizeSmall: {
            height: 'max(var(--density-control-h-sm), var(--target-min))',
            paddingInline: 12,
            fontSize: 'var(--text-label)',
          },
          sizeLarge: {
            height: 'max(44px, var(--target-min))',
            paddingInline: 20,
            fontSize: '0.9375rem',
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-control)',
            transition: 'var(--transition-colors)',
            variants: [
              {
                props: { color: 'default' },
                style: {
                  color: 'var(--text-secondary)',
                  '&:hover': { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' },
                },
              },
            ],
          },
          sizeMedium: {
            width: 'var(--density-control-h)',
            height: 'var(--density-control-h)',
            minWidth: 'var(--target-min)',
            padding: 0,
          },
          sizeSmall: {
            width: 'max(var(--density-control-h-sm), var(--target-min))',
            height: 'max(var(--density-control-h-sm), var(--target-min))',
            padding: 0,
          },
        },
      },

      // Campos: 6px de radio, borde a 3:1, invalido en lavado ladrillo.
      MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          // La zona que abre el menu es este div, no la raiz del campo: tiene
          // que medir el alto del control (44px con puntero coarse), no 40.
          select: { minHeight: 'var(--density-control-h)', boxSizing: 'border-box' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-field)',
            backgroundColor: 'var(--bg-raised)',
            color: 'var(--text-primary)',
            fontSize: 'var(--control-font)',
            transition: 'var(--transition-colors)',
            '&:not(.MuiInputBase-multiline)': { minHeight: 'var(--density-control-h)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-tertiary)' },
            // Foco del campo: borde de 2px en el color de foco (>= 3:1). Sin anillo
            // exterior: la etiqueta se apoya en el borde superior y el anillo la
            // tachaba.
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--focus-color)',
              borderWidth: 2,
            },
            '&.Mui-error': { backgroundColor: 'var(--status-danger-wash)' },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--status-danger-solid)',
            },
            '&.Mui-disabled': { backgroundColor: 'var(--bg-sunken)' },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border-hairline)',
            },
            '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
          },
          notchedOutline: { borderColor: 'var(--border-strong)' },
          input: {
            '&::placeholder': { color: 'var(--text-tertiary)', opacity: 1 },
            '&.Mui-disabled': {
              color: 'var(--text-disabled)',
              WebkitTextFillColor: 'var(--text-disabled)',
            },
          },
          multiline: { lineHeight: 'var(--leading-body)' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 'var(--control-font)',
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: 'var(--accent-text)' },
            '&.Mui-error': { color: 'var(--status-danger-text)' },
            '&.Mui-disabled': { color: 'var(--text-disabled)' },
          },
          shrink: { fontWeight: 600, color: 'var(--text-primary)' },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 'var(--text-body-dense)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            '&.Mui-focused': { color: 'var(--text-primary)' },
            '&.Mui-error': { color: 'var(--status-danger-text)' },
          },
          asterisk: { color: 'var(--status-danger-text)' },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: 'var(--text-label)',
            marginInline: 2,
            color: 'var(--text-secondary)',
            '&.Mui-error': { color: 'var(--status-danger-text)', fontWeight: 600 },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: { fontSize: 'var(--text-body-dense)', color: 'var(--text-primary)' },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: 'var(--border-strong)',
            // 24 + 2x9 = 42px de MUI; con puntero coarse, 44.
            '@media (pointer: coarse)': { padding: 10 },
            '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'var(--accent-emphasis)' },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: 'var(--border-strong)',
            '@media (pointer: coarse)': { padding: 10 },
            '&.Mui-checked': { color: 'var(--accent-emphasis)' },
          },
        },
      },

      // Interruptor: la raiz de MUI mide 38px y recorta (overflow hidden), asi
      // que la zona pulsable no pasa de 38. Con puntero coarse sube a 44 y el
      // pulgar y la pista se recentran.
      MuiSwitch: {
        styleOverrides: {
          root: { '@media (pointer: coarse)': { height: 44, paddingTop: 15, paddingBottom: 15 } },
          switchBase: { '@media (pointer: coarse)': { top: 3 } },
          input: { '@media (pointer: coarse)': { top: -3, height: 44 } },
        },
      },

      // Tarjeta: 20px, filete, plana (el plano separa antes que la sombra).
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'var(--bg-raised)',
            color: 'var(--text-primary)',
          },
          rounded: { borderRadius: 'var(--radius-card)' },
          outlined: { border: '1px solid var(--border-card)' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: '1px solid var(--border-card)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'none',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: 'var(--density-card-p)', paddingBottom: 16 },
          title: {
            fontFamily: 'var(--font-head)',
            fontSize: 'var(--text-panel-title)',
            fontWeight: 700,
            letterSpacing: 'var(--tracking-snug)',
            lineHeight: 'var(--leading-snug)',
          },
          subheader: {
            marginTop: 4,
            fontSize: 'var(--text-body-dense)',
            color: 'var(--text-secondary)',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 'var(--density-card-p)',
            '&:last-child': { paddingBottom: 'var(--density-card-p)' },
          },
        },
      },

      // Tabla de lectura: cabecera hundida en 13px/600, filas de 40px, filete
      // sin cebra, cifras tabulares.
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 'var(--density-font-body)',
            fontVariantNumeric: 'tabular-nums',
            padding: 'var(--density-row-y) var(--density-row-x)',
            borderBottom: '1px solid var(--border-hairline)',
            color: 'var(--text-secondary)',
            verticalAlign: 'middle',
            height: 'var(--density-row-h)',
          },
          head: {
            padding: '8px var(--density-row-x)',
            height: 'auto',
            fontSize: 'var(--text-label)',
            fontWeight: 600,
            letterSpacing: 0,
            textTransform: 'none',
            color: 'var(--text-secondary)',
            backgroundColor: 'var(--bg-sunken)',
            whiteSpace: 'nowrap',
          },
          stickyHeader: { backgroundColor: 'var(--bg-sunken)' },
          sizeSmall: { padding: 'var(--density-row-y) var(--density-row-x)' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color var(--dur-instant) var(--ease-out)',
            '&:last-child td': { borderBottom: 0 },
            '&.MuiTableRow-hover:hover': { backgroundColor: 'var(--bg-hover)' },
            // La casilla es el control: la fila solo cambia de plano, sin check.
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'var(--bg-selected)' },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            '&:hover': { color: 'var(--text-primary)' },
            '&.Mui-active': { color: 'var(--accent-text)' },
          },
        },
      },

      // Insignias en pildora: 13px/600, borde de 1px. Todo estado lleva palabra
      // Y marca (Pulso): el exito lleva ✓ (lo pone el tema si el chip no trae
      // icono); aviso !, peligro ✕ y en curso ◐ los pone `MarcaDeEstado` como
      // icono de `Insignia`. La seleccion (color primary) NUNCA lleva marca.
      MuiChip: {
        styleOverrides: {
          root: {
            height: 'auto',
            minHeight: 24,
            padding: '3px 10px',
            gap: 4,
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--bg-sunken)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-label)',
            fontWeight: 600,
            lineHeight: 'var(--leading-snug)',
            transition: 'var(--transition-colors)',
            '&.MuiChip-clickable:hover': { backgroundColor: 'var(--bg-hover)' },
            // Una insignia que filtra es un control: 44px con puntero coarse.
            '&.MuiChip-clickable': { minHeight: 'max(24px, var(--target-min))' },
            variants: [
              {
                props: { variant: 'outlined' },
                style: {
                  backgroundColor: 'var(--bg-raised)',
                  borderColor: 'var(--border-subtle)',
                },
              },
              {
                props: { color: 'primary' },
                style: {
                  backgroundColor: 'var(--accent-wash)',
                  color: 'var(--accent-text)',
                  borderColor: 'var(--accent-mist)',
                  '&.MuiChip-clickable:hover': { backgroundColor: 'var(--accent-wash)' },
                },
              },
              {
                props: { color: 'success' },
                style: {
                  ...insignia('success'),
                  '&:not(:has(.MuiChip-icon))::before': {
                    content: '"\\2713"',
                    fontWeight: 700,
                    marginInlineStart: -2,
                  },
                },
              },
              {
                props: { color: 'warning' },
                style: insignia('warning'),
              },
              {
                props: { color: 'error' },
                style: insignia('danger'),
              },
              { props: { color: 'info' }, style: insignia('info') },
            ],
          },
          label: { padding: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
          sizeSmall: { minHeight: 24, fontSize: 'var(--text-label)' },
          icon: { margin: 0, marginInlineStart: -2, fontSize: 14, color: 'inherit' },
          deleteIcon: {
            margin: 0,
            marginInlineEnd: -4,
            fontSize: 16,
            color: 'inherit',
            opacity: 0.7,
            '&:hover': { color: 'inherit', opacity: 1 },
          },
        },
      },

      // Pestanas en pildora (Pulso): la vigente lleva fondo de seleccion, texto
      // de acento en semibold y contorno de 2px; nunca solo color.
      MuiTabs: {
        defaultProps: { variant: 'scrollable', scrollButtons: false },
        styleOverrides: {
          root: { minHeight: 0 },
          flexContainer: { gap: 4 },
          indicator: { display: 'none' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 'max(40px, var(--target-min))',
            minWidth: 0,
            padding: '8px 12px',
            fontSize: 'var(--text-body-dense)',
            fontWeight: peso('var(--weight-medium)'),
            textTransform: 'none',
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
            borderRadius: 'var(--radius-control)',
            border: '2px solid transparent',
            transition: 'var(--transition-colors)',
            '&:hover': { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' },
            '&.Mui-selected': {
              color: 'var(--accent-text)',
              fontWeight: 600,
              backgroundColor: 'var(--bg-selected)',
              borderColor: 'var(--accent-emphasis)',
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            gap: 4,
            flexWrap: 'wrap',
            '& .MuiToggleButtonGroup-grouped': {
              margin: 0,
              borderRadius: 'var(--radius-control)',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            minHeight: 'max(32px, var(--target-min))',
            padding: '4px 12px',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-control)',
            backgroundColor: 'var(--bg-raised)',
            fontSize: 'var(--text-body-dense)',
            fontWeight: peso('var(--weight-medium)'),
            lineHeight: 1.3,
            textTransform: 'none',
            color: 'var(--text-secondary)',
            transition: 'var(--transition-colors)',
            '&:hover': { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'var(--bg-selected)',
              color: 'var(--accent-text)',
              borderColor: 'var(--accent-emphasis)',
              fontWeight: 600,
            },
          },
          sizeSmall: { minHeight: 'max(28px, var(--target-min))', padding: '2px 10px' },
        },
      },

      // Dialogos: marco de 24px, sombra modal, velo del sistema. En movil el
      // dialogo ocupa el ancho con 16px de canal y se desplaza por dentro, para
      // que el teclado virtual no tape las acciones.
      MuiBackdrop: {
        styleOverrides: {
          root: {
            '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'var(--bg-overlay)' },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--radius-frame)',
            border: '1px solid var(--border-card)',
            boxShadow: 'var(--shadow-4)',
            '@media (max-width: 599.95px)': {
              margin: 16,
              width: 'calc(100% - 32px)',
              maxHeight: 'calc(100dvh - 32px)',
            },
          },
          paperFullScreen: {
            '@media (max-width: 599.95px)': {
              margin: 0,
              width: '100%',
              maxHeight: '100dvh',
              borderRadius: 0,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: 'var(--font-head)',
            fontSize: 'var(--text-panel-title)',
            fontWeight: 600,
            lineHeight: 'var(--leading-snug)',
            letterSpacing: 'var(--tracking-snug)',
            padding: '20px 24px 8px',
          },
        },
      },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px 20px',
            gap: 8,
            flexWrap: 'wrap-reverse',
            '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          },
        },
      },

      // Menus y paneles flotantes: 8px, filete sutil, sombra flotante.
      MuiPopover: { styleOverrides: { paper: panelFlotante } },
      MuiMenu: {
        styleOverrides: {
          paper: { ...panelFlotante, minWidth: 200 },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 'max(36px, var(--target-min))',
            padding: '4px 10px',
            gap: 10,
            borderRadius: 'var(--radius-xs)',
            fontSize: 'var(--text-body-dense)',
            color: 'var(--text-primary)',
            '&:hover': { backgroundColor: 'var(--bg-hover)' },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'var(--bg-selected)',
              color: 'var(--accent-text)',
              fontWeight: 600,
            },
            '&.Mui-focusVisible': { backgroundColor: 'var(--bg-hover)' },
            '& .MuiListItemIcon-root': { minWidth: 0, color: 'var(--text-secondary)' },
            '& .MuiSvgIcon-root': { fontSize: 18 },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { ...panelFlotante, marginTop: 6 },
          listbox: {
            padding: 6,
            '& .MuiAutocomplete-option': {
              minHeight: 'max(36px, var(--target-min))',
              borderRadius: 'var(--radius-xs)',
              fontSize: 'var(--text-body-dense)',
            },
            '& .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'var(--bg-hover)' },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: 'var(--bg-selected)',
              color: 'var(--accent-text)',
            },
          },
        },
      },

      MuiTooltip: {
        defaultProps: { enterDelay: 400 },
        styleOverrides: {
          tooltip: {
            backgroundColor: 'var(--bg-inverted)',
            color: 'var(--text-on-inverted)',
            fontSize: 'var(--text-label)',
            fontWeight: 500,
            lineHeight: 1.4,
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
          },
          arrow: { color: 'var(--bg-inverted)' },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            border: '1px solid',
            borderRadius: 'var(--radius-card)',
            fontSize: 'var(--text-body-dense)',
            alignItems: 'flex-start',
            '& .MuiAlert-message': { paddingBlock: 9, minWidth: 0 },
            '& .MuiAlert-action': { paddingTop: 2 },
            '& .MuiAlert-icon': { color: 'inherit', opacity: 1 },
          },
          standardSuccess: insignia('success'),
          standardWarning: insignia('warning'),
          standardError: insignia('danger'),
          standardInfo: insignia('info'),
          outlinedSuccess: {
            color: 'var(--status-success-text)',
            borderColor: 'var(--status-success-border)',
          },
          outlinedWarning: {
            color: 'var(--status-warning-text)',
            borderColor: 'var(--status-warning-border)',
          },
          outlinedError: {
            color: 'var(--status-danger-text)',
            borderColor: 'var(--status-danger-border)',
          },
          outlinedInfo: {
            color: 'var(--status-info-text)',
            borderColor: 'var(--status-info-border)',
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--bg-inverted)',
            color: 'var(--text-on-inverted)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-3)',
          },
        },
      },

      MuiLink: {
        defaultProps: { underline: 'always' },
        styleOverrides: {
          root: {
            color: 'var(--accent-text)',
            textDecorationColor: 'var(--accent-mist)',
            textUnderlineOffset: '0.2em',
            '&:hover': { textDecorationColor: 'currentColor' },
            // Enlace dentro de una frase: el relleno de un elemento en linea
            // agranda la zona pulsable sin mover el renglon; el margen negativo
            // devuelve el ancho. Con puntero coarse llega a 44px.
            '@media (pointer: coarse)': {
              paddingBlock: 14,
              paddingInline: 4,
              marginInline: -4,
            },
          },
        },
      },

      // Destino de navegacion: activo = plano de seleccion + barra de 3px + peso.
      // Sin check.
      MuiListItemButton: {
        styleOverrides: {
          root: {
            position: 'relative',
            minHeight: 'max(36px, var(--target-min))',
            padding: '6px 10px',
            gap: 10,
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-body-dense)',
            fontWeight: peso('var(--weight-medium)'),
            transition: 'var(--transition-colors)',
            '&:hover': { backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)' },
            '&.Mui-selected, &.Mui-selected:hover, &[aria-current="page"]': {
              backgroundColor: 'var(--bg-selected)',
              color: 'var(--accent-text)',
              fontWeight: 600,
            },
            '&.Mui-selected::before, &[aria-current="page"]::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--accent-emphasis)',
            },
            '& .MuiListItemIcon-root': { color: 'inherit' },
          },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--accent-wash)',
            color: 'var(--accent-text)',
            fontSize: '0.8125rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
          },
        },
      },
      // Pulso de opacidad, no barrido. `--bg-active` y no `--bg-sunken`:
      // sobre tarjeta blanca el hundido casi no se distingue.
      MuiSkeleton: {
        defaultProps: { animation: 'pulse' },
        styleOverrides: { root: { backgroundColor: 'var(--bg-active)' } },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: 'var(--border-hairline)' } } },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--bg-surface)',
            backgroundImage: 'none',
            borderColor: 'var(--border-hairline)',
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: 'auto',
            minHeight: 56,
            backgroundColor: 'var(--bg-chrome)',
            borderTop: '1px solid var(--border-hairline)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 0,
            minHeight: 56,
            padding: '6px 4px',
            color: 'var(--text-secondary)',
            gap: 2,
            '&.Mui-selected': { color: 'var(--accent-text)' },
          },
          label: {
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            '&.Mui-selected': { fontSize: 'var(--text-meta)', fontWeight: 700 },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            minWidth: 'max(32px, var(--target-min))',
            height: 'max(32px, var(--target-min))',
            borderRadius: 'var(--radius-control)',
            fontSize: 'var(--text-body-dense)',
            color: 'var(--text-secondary)',
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'var(--bg-selected)',
              color: 'var(--accent-text)',
              fontWeight: 700,
              boxShadow: 'inset 0 0 0 1px var(--accent-emphasis)',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 3, borderRadius: 0, backgroundColor: 'var(--accent-wash)' },
          bar: { backgroundColor: 'var(--accent-emphasis)' },
        },
      },
      MuiCircularProgress: { defaultProps: { color: 'inherit' } },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: '1px solid var(--border-card)',
            borderRadius: 'var(--radius-card)',
            '&::before': { display: 'none' },
            '& + &': { marginTop: 8 },
          },
        },
      },

      MuiContainer: {
        styleOverrides: {
          root: { maxWidth: `${CHASIS.anchoTabla}px` },
        },
      },
    },
  });
}
